import { useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { AppContext } from "../../context/EditSessionsContext";
import { Button, Heading, Text } from "@foris/avocado-suite";
import { ContextApp, IContextApp } from "@config/Context/contextApp";
import { ContextEDH } from "@context/ContextEDH";
import { Types } from "../../context/formData.reducer";
import { useHistory } from "react-router-dom";
import { IWeek } from "@modules/sections/ISections";
import { allSessionsCanBeSaved, sessionIsSavable } from "../../utils/context";
import { isSessionWeekOutsideThreshold } from "../../utils/isOutsideThreshold";
import { needsPastFork } from "../../utils/needsPastFork";
import { useForkOrchestration } from "../../hooks/useForkOrchestration";
import { useActiveProposal } from "../../hooks/useActiveProposal";
import { useIsEditorLocked } from "../../hooks/useIsEditorLocked";
import { useProposalMode } from "@services/collections/assignment-proposal/assignmentProposalServices";
import type { PreflightIntent } from "@services/types/assignment-proposal/assignmentProposalTypes";
import { canSaveDraft } from "@utils/assignmentProposal";
import Modal from "@components/modal";
import ProposalRejectButton from "../proposal-reject-button";
import useContextUrl from "@common/hooks/useContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import styles from "./assignationHeader.module.scss";
import { useTranslation } from "react-i18next";

interface AssignationHeaderProps {
  viewSwitch?: ReactNode;
  locationCurrentSelectedWeek?: IWeek;
  onSubmitValidations?: () => void;
  /** Proposal mode only: save the current changes as a draft instead of validating them. */
  onSaveDraft?: () => void;
  /** A draft request is running: the draft button shows its loading state. */
  isSavingDraft?: boolean;
  onShowValidationWarnings?: (showWarnings: boolean, intent?: PreflightIntent) => void;
}

const AssignationHeader = ({
  viewSwitch,
  locationCurrentSelectedWeek,
  onSubmitValidations,
  onSaveDraft,
  isSavingDraft = false,
  onShowValidationWarnings,
}: AssignationHeaderProps) => {
  const history = useHistory();
  const { id } = useContextUrl();
  const { editorUrl } = useNavigationUrl();
  const { state, dispatch } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const context: IContextApp = useContext(ContextApp);
  const { runFork } = useForkOrchestration();
  const { isProposalMode, isResolving } = useProposalMode();
  const isLocked = useIsEditorLocked();
  const activeProposal = useActiveProposal();
  // Only a proposal that was never sent can be saved as a draft (not a correction, not a review).
  const canSaveAsDraft = isProposalMode && canSaveDraft(activeProposal);
  const [forkModalIntent, setForkModalIntent] = useState<PreflightIntent | null>(null);
  const [pendingIntentAfterFork, setPendingIntentAfterFork] = useState<PreflightIntent | null>(
    null,
  );

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const thresholdDate = state?.form?.blockingThreshold ?? null;
  const canEditPastSessions = !!stateEDH?.base?.base?.user?.abilities?.can_edit_past_sessions;

  const sessionsToFork = useMemo(
    () => needsPastFork(state?.form, state?.link, thresholdDate, canEditPastSessions),
    [state?.form, state?.link, thresholdDate, canEditPastSessions],
  );

  const selectedSessionsLabel = useMemo(() => {
    const selectedSessionsLength = state?.form?.selectedSessions?.length ?? 0;

    if (selectedSessionsLength === 1) {
      return t("assignation-edit.header.singular-selected-sessions");
    }

    return t("assignation-edit.header.selected-sessions", {
      count: selectedSessionsLength,
    });
  }, [state?.form?.selectedSessions, language]);

  const userCanEditOrDeleteSessions = context?.user?.abilities?.can_edit_or_delete_sessions;
  const numberOfSavedSessions = state?.form?.savedSessions?.length ?? 0;
  const numberOfSessionsToCreate = state?.form?.sessionsToCreate?.length ?? 0;

  const numberOfDeletedSessions = useMemo(() => {
    return Object.keys(state?.form?.sessionsToDelete ?? {}).length;
  }, [state?.form?.sessionsToDelete]);

  const someCreatedSessionIsSavable = useMemo(() => {
    const anyCreatedSessionIsSavable = (state?.form?.sessionsToCreate ?? []).some(sessionIsSavable);
    const someSessionHasBeenSaved = Boolean(numberOfSavedSessions);
    const someSessionHasBeenDeleted = Boolean(numberOfDeletedSessions);
    return someSessionHasBeenSaved || someSessionHasBeenDeleted || anyCreatedSessionIsSavable;
  }, [state?.form?.savedSessions, state?.form?.sessionsToCreate]);

  const allSavedSessionsAreSavable = useMemo(
    () => (state?.form?.savedSessions ?? []).every(sessionIsSavable),
    [state?.form?.savedSessions],
  );

  const allSessionsAreDeleted = useMemo(
    () =>
      (state?.form?.selectedSessions ?? []).every(
        session => state?.form?.sessionsToDelete?.[session.id ?? "-"],
      ),
    [state?.form?.selectedSessions, state?.form?.sessionsToDelete],
  );

  const someSelectedSessionHasBeenDeleted = useMemo(
    () =>
      (state?.form?.selectedSessions ?? []).some(
        session => state?.form?.sessionsToDelete?.[session.id ?? "-"],
      ),
    [state?.form?.selectedSessions, state?.form?.sessionsToDelete],
  );

  // The mode is unknown while resolving, and a sent proposal locks the editor: no pre-flight may
  // start. While a proposal request is running (global loading) a second one must not start either;
  // direct-save users keep exactly today's enablement.
  const isActionBlocked = isLocked || isResolving || (isProposalMode && !!state?.page?.loading);

  const proceedWithPreflight = (intent: PreflightIntent) => {
    if (!numberOfSavedSessions && !numberOfSessionsToCreate && !numberOfDeletedSessions) {
      return;
    }

    if (
      allSessionsCanBeSaved(state?.form?.sessionsToCreate) &&
      !state?.form?.selectedCreateSession
    ) {
      if (intent === "draft") onSaveDraft?.();
      else onSubmitValidations?.();
      return;
    }

    onShowValidationWarnings?.(true, intent);
  };

  const handlePreflight = (intent: PreflightIntent) => {
    if (sessionsToFork.length > 0) {
      setForkModalIntent(intent);
      return;
    }

    proceedWithPreflight(intent);
  };

  const handlePastForkConfirm = () => {
    if (isLocked) return;

    const intent = forkModalIntent;
    setForkModalIntent(null);
    runFork(
      (week, session) => isSessionWeekOutsideThreshold(thresholdDate, week, session),
      "assignation-edit.past-sessions.fork-toast",
      sessionsToFork,
    );
    setPendingIntentAfterFork(intent);
  };

  // Invariant: `pendingIntentAfterFork` lives for exactly one render cycle.
  // It is raised right after the fork dispatch (`ForkSelectedSessions`) and
  // consumed here on the next render, once the
  // reducer has committed the new state. We need this defer because
  // `proceedWithPreflight` closes over `state?.form?.{savedSessions,
  // sessionsToCreate,...}` — calling it synchronously would validate against
  // the pre-fork snapshot. Do NOT call `setPendingIntentAfterFork(...)`
  // from anywhere else: a duplicate raise would silently trigger a second
  // submit.
  useEffect(() => {
    if (!pendingIntentAfterFork) return;
    const intent = pendingIntentAfterFork;
    setPendingIntentAfterFork(null);
    proceedWithPreflight(intent);
  }, [pendingIntentAfterFork]);

  const handlePastForkCancel = () => {
    setForkModalIntent(null);
  };

  const hasNothingToSend =
    (numberOfSavedSessions === 0 &&
      numberOfSessionsToCreate === 0 &&
      numberOfDeletedSessions === 0) ||
    !someCreatedSessionIsSavable ||
    !allSavedSessionsAreSavable;

  return (
    <header className={styles.assignationHeader}>
      <div className={styles.assignationHeader_leftContent}>
        {viewSwitch}
        <Heading type="h3">{selectedSessionsLabel}</Heading>

        <div className={styles.actions}>
          {userCanEditOrDeleteSessions && (
            <Button
              size="sm"
              leftIcon="trash"
              variant="ghost"
              disabled={allSessionsAreDeleted || isLocked}
              onClick={() => {
                dispatch({
                  type: Types.RemoveSelectedSessions,
                  payload: state?.form?.selectedSessions,
                });
              }}
            >
              {t("assignation-edit.header.delete")}
            </Button>
          )}

          <Button
            size="sm"
            leftIcon="repeat"
            variant="ghost"
            disabled={
              isLocked ||
              !state?.form?.selectedSessions?.length ||
              (!numberOfSavedSessions &&
                !numberOfSessionsToCreate &&
                !someSelectedSessionHasBeenDeleted)
            }
            onClick={() => {
              dispatch({
                type: Types.UndoEditionOverSelectedSessions,
                payload: state?.form?.selectedSessions,
              });
            }}
          >
            {t("assignation-edit.header.restore-changes")}
          </Button>

          {userCanEditOrDeleteSessions && (
            <Button
              size="sm"
              leftIcon="copy"
              variant="ghost"
              disabled={isLocked || !state?.form?.selectedSessions?.length}
              onClick={() => {
                dispatch({ type: Types.AddSessionsToCreate, payload: "cloned" });
              }}
            >
              {t("assignation-edit.header.duplicate")}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.assignationHeader_rightContent}>
        <Button
          size="md"
          variant="ghost"
          onClick={() => {
            history.push(editorUrl("link", id), {
              currentSelectedWeek: locationCurrentSelectedWeek,
              sessionId: undefined,
            });
          }}
        >
          {t("assignation-edit.header.go-to-league")}
        </Button>
        <ProposalRejectButton />
        {canSaveAsDraft && (
          <Button
            size="md"
            variant="secondary"
            loading={isSavingDraft}
            disabled={hasNothingToSend || isActionBlocked}
            onClick={() => handlePreflight("draft")}
          >
            {t("assignation-edit.proposal.actions.save-draft")}
          </Button>
        )}
        <Button
          size="md"
          variant="primary"
          disabled={hasNothingToSend || isActionBlocked}
          onClick={() => handlePreflight("validate")}
        >
          {isProposalMode
            ? t("assignation-edit.proposal.actions.validate")
            : t("assignation-edit.header.validate")}
        </Button>
      </div>

      <Modal
        typeState="confirm"
        title={t("assignation-edit.past-sessions.fork-modal.title")}
        show={forkModalIntent !== null}
        textButtonPrincipal={t("assignation-edit.past-sessions.fork-modal.confirm")}
        textButtonSecondary={t("assignation-edit.past-sessions.fork-modal.cancel")}
        onClickPrincipal={handlePastForkConfirm}
        onClickSecondary={handlePastForkCancel}
        onClose={handlePastForkCancel}
      >
        <Text>{t("assignation-edit.past-sessions.fork-modal.body.main")}</Text>
        <ul className={styles.forkList}>
          <li className={styles.forkList_item}>
            <Text weight="medium" className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.original-sessions.title")}:
            </Text>{" "}
            <Text className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.original-sessions.description")}
            </Text>
          </li>
          <li className={styles.forkList_item}>
            <Text weight="medium" className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.new-sessions.title")}:
            </Text>{" "}
            <Text className={styles.forkList_itemText}>
              {t("assignation-edit.past-sessions.fork-modal.body.new-sessions.description")}
            </Text>
          </li>
        </ul>
      </Modal>
    </header>
  );
};

export default AssignationHeader;
